/**
 * State for the Common Balance Pool builder, following the package's builder-state convention
 * (cf. TPAllocationState / CalculationBuilderState): entity + isLoading/error, a `needsRefresh` flag
 * that drives the fetch effect, and the cross-component modal / lifecycle state.
 */
import { SelectProps } from '@amzn/awsui-components-react/polaris/select';
import { CommonBalancePool, CommonBalancePoolColumn, CommonBalancePoolExecutionStatus, CommonBalancePoolRow } from 'src/models/multidimensional-allocation/CommonBalancePool';
import { SegmentTableDefinition } from 'src/models/segment-cdt/SegmentCdtTypes';

/**
 * One Step-1 input row: a source object and the columns it contributes (matching the CBP mockup).
 * Owned here in the service layer so `editingRow` doesn't create a service→component dependency;
 * the component-side helpers in `cbpInputs.ts` import and re-export this type.
 */
export interface InputRow {
    num: number;
    sourceType?: string;
    sourceObject: string;
    sourceKey: string;
    /** Display names, for the Columns badges. */
    columns: string[];
    /** The underlying INPUT columns backing this row, so Edit can rebuild them. */
    cols: CommonBalancePoolColumn[];
}

export interface CommonBalancePoolBuilderState {
    pool?: CommonBalancePool;
    isLoading: boolean;
    error?: string;
    /** Set true to (re)fetch the pool; the reducer clears it on SET_POOL. */
    poolNeedsRefresh: boolean;

    /** True while a column persist (UpdateCommonBalancePool) is in flight. */
    saving: boolean;
    saveError?: string;

    /** True while Step 2 (calculated columns) has unsaved edits — guards the lifecycle/input actions. */
    step2Dirty: boolean;

    /** Add/Edit-input modal. `editingRow` present => edit mode for that input. */
    addModalVisible: boolean;
    editingRow?: InputRow;

    /** Selected accounting period (for Execute). */
    period?: SelectProps.Option | null;

    /** Which top-level lifecycle action is in flight. */
    actionBusy: 'save' | 'submit' | 'execute' | 'approve' | 'reject' | '';
    actionError?: string;
    actionNotice?: string;

    /**
     * Live status of the current period's Execute run, polled from GetCommonBalancePoolExecutionStatus
     * (LLD §12.4) until a terminal state or the client-side timeout. Session-scoped and keyed to the run
     * the user triggered — cleared on a period switch, and gone on reload (recovering a period's last
     * status without an executionId is the deferred by-(poolId, period) backend read).
     */
    executionStatus?: CommonBalancePoolExecutionStatus;
    /**
     * True once the poll gave up without a terminal state — the 30-min client-side timeout, or repeated
     * status-read failures. Drives the "Reload status" recovery Alert and unblocks the Execute button. The
     * run may still be going server-side.
     */
    executionTimedOut?: boolean;

    /**
     * The segment-CDT list (the Step-1 input sources), fetched once by the builder and shared via
     * context: the builder derives an input source-name map from it, and the Add-input modal reads the
     * same list for its picker (rather than fetching it again).
     */
    segmentTables?: SegmentTableDefinition[];
    segmentTablesLoading?: boolean;
    segmentTablesError?: string;

    /**
     * Step-3 preview slice: the selected period's materialized rows, read a page at a time via
     * getCommonBalancePoolRows. Held here (not as component-local state) so the whole builder's state lives
     * in one reducer, per the package convention.
     */
    resultsRows: CommonBalancePoolRow[];
    resultsNextToken?: string;
    resultsTotalRowCount?: number;
    resultsLoading: boolean;
    resultsError?: string;
    /**
     * Bumped to force the Step-3 preview to reset + refetch the selected period — e.g. after an Execute,
     * so the newly materialized rows load without the user re-selecting the period. CbpResultsSection's
     * fetch effect depends on it alongside period/poolId.
     */
    resultsRefreshToken: number;

    /** Step-3 "Download" export: true while the presigned-URL request is in flight. */
    resultsDownloading: boolean;
    resultsDownloadError?: string;
}

export const initialState: CommonBalancePoolBuilderState = {
    isLoading: true,
    poolNeedsRefresh: true,
    saving: false,
    step2Dirty: false,
    addModalVisible: false,
    actionBusy: '',
    resultsRows: [],
    resultsLoading: false,
    resultsRefreshToken: 0,
    resultsDownloading: false,
};

export const getInitialState = (): CommonBalancePoolBuilderState => ({ ...initialState });
